import { useState } from "react";
import { useMediaQuery } from "react-responsive";
import { format } from "date-fns";

import { AttachmentModel, AssetModel } from "@interfaces/index";
import {
  QUERY_WIDTHS,
  PURCHASE_TYPES,
  formatAmount,
  getLabelFileAgreementField,
} from "@utils/index";
import { useCommonStore } from "@stores/index";
import {
  Drawer,
  DrawerHeader,
  DrawerScrollableContainer,
  XSvg,
  Button,
  EditSvg,
  FileItem,
  AssetItem,
  FilePreview,
  ChecksSvg,
  TrashXSvg,
  UploadLaterBannerAssets,
} from "@components/index";
import { AssetDisposal } from "@pages/index";

export const AssetDetails = ({
  asset,
  onClose,
  onEdit,
}: {
  asset?: AssetModel;
  onClose: () => void;
  onEdit: () => void;
}) => {
  const isMobile = useMediaQuery({ query: QUERY_WIDTHS.md });
  const setCommonState = useCommonStore(({ setState }) => setState);

  const [attachmentPreview, setAttachmentPreview] = useState<AttachmentModel>();

  const [isDisposing, setIsDisposing] = useState(false);

  const handleClose = () => {
    setCommonState({ isDrawerShown: false });
    onClose();
  };

  return (
    asset && (
      <Drawer className="drawer-asset-details">
        <DrawerHeader
          onClose={handleClose}
          className="h-[70px]"
          title={
            <div className="flex flex-col items-center md:items-start">
              <AssetItem type={asset.type} size="big" name={asset.name} />
              {isMobile && asset.userBusiness?.name && (
                <div className="text-black-54 md:text-300 md:font-bold">
                  {asset.userBusiness?.name}
                </div>
              )}
            </div>
          }
        />
        {!isMobile && asset.userBusiness?.name && (
          <div className="px-4 pt-6 text-300 font-bold capitalize text-black-54 md:px-14 md:pt-1">
            {asset.userBusiness?.name}
          </div>
        )}
        <DrawerScrollableContainer className="max-h-[calc(100dvh_-_70px)]">
          <UploadLaterBannerAssets asset={asset} className="md:hidden" />
          <div className="flex items-center justify-between pb-4 pt-6 text-300 font-bold text-black-100 md:pt-10 md:text-400">
            Asset information
            {asset.isDefault && (
              <div className="flex items-center gap-x-1.5 rounded-full bg-primary-108 px-2.5 py-1.5 text-primary-100 md:px-4">
                <ChecksSvg />
                Default
              </div>
            )}
          </div>
          <UploadLaterBannerAssets asset={asset} className="hidden md:block" />
          <div className="flex flex-col gap-x-6 gap-y-2 pb-4 pt-4 md:flex-row md:pb-6">
            <div className="text-black-70 md:w-60 md:shrink-0 md:text-300">
              Asset Type
            </div>
            <div className="text-black-100 md:text-300">
              <AssetItem type={asset.type} justTypeName />
            </div>
          </div>
          <div className="flex flex-col gap-x-6 gap-y-2 border-t border-t-black-16 pb-4 pt-4 md:flex-row md:pb-6 md:pt-6">
            <div className="text-black-70 md:w-60 md:shrink-0 md:text-300">
              {asset.type === "vehicle" && "Registration Number"}
              {asset.type === "rental_property" && "Address"}
              {asset.type === "other" && "Description"}
            </div>
            <div className="capitalize text-black-100 md:text-300">
              {asset.name}
            </div>
          </div>
          <div className="flex flex-col gap-x-6 gap-y-2 border-t border-t-black-16 pb-4 pt-4 md:flex-row md:pb-6 md:pt-6">
            <div className="text-black-70 md:w-60 md:shrink-0 md:text-300">
              Purchase Type
            </div>
            <div className="text-black-100 md:text-300">
              {PURCHASE_TYPES[asset.purchaseType]}
            </div>
          </div>
          <div className="flex flex-col gap-x-6 gap-y-2 border-t border-t-black-16 pb-4 pt-4 md:flex-row md:pb-6 md:pt-6">
            <div className="text-black-70 md:w-60 md:shrink-0 md:text-300">
              {`${asset.purchaseType === "lease" ? "Lease" : "Purchase"} date`}
            </div>
            <div className="text-black-100 md:text-300">
              {format(new Date(asset.date), "dd.MM.yyyy")}
            </div>
          </div>
          {asset.firstLetAt && (
            <div className="flex flex-col gap-x-6 gap-y-2 border-t border-t-black-16 pb-4 pt-4 md:flex-row md:pb-6 md:pt-6">
              <div className="text-black-70 md:w-60 md:shrink-0 md:text-300">
                Date first let
              </div>
              <div className="text-black-100 md:text-300">
                {format(new Date(asset.firstLetAt), "dd.MM.yyyy")}
              </div>
            </div>
          )}
          {asset.type === "rental_property" && (
            <div className="flex flex-col gap-x-6 gap-y-2 border-t border-t-black-16 pb-4 pt-4 md:flex-row md:pb-6 md:pt-6">
              <div className="text-black-70 md:w-60 md:shrink-0 md:text-300">
                Joint
              </div>
              <div className="text-black-100 md:text-300">
                {asset.jointShare ? "Yes" : "No"}
              </div>
            </div>
          )}
          {asset.jointShare && (
            <div className="flex flex-col gap-x-6 gap-y-2 border-t border-t-black-16 pb-4 pt-4 md:flex-row md:pb-6 md:pt-6">
              <div className="text-black-70 md:w-60 md:shrink-0 md:text-300">
                Share
              </div>
              <div className="text-black-100 md:text-300">
                {asset.jointShare}%
              </div>
            </div>
          )}
          {asset.cost && (
            <div className="flex flex-col gap-x-6 gap-y-2 border-t border-t-black-16 pb-4 pt-4 md:flex-row md:pb-6 md:pt-6">
              <div className="text-black-70 md:w-60 md:shrink-0 md:text-300">
                Cost
              </div>
              <div className="capitalize text-black-100 md:text-300">
                {formatAmount(asset.cost)}
              </div>
            </div>
          )}
          {asset.comment && (
            <div className="flex flex-col gap-x-6 gap-y-2 border-t border-t-black-16 pb-4 pt-4 md:flex-row md:pb-6 md:pt-6">
              <div className="text-black-70 md:w-60 md:shrink-0 md:text-300">
                Comment
              </div>
              <div className="capitalize text-black-100 md:text-300">
                {asset.comment}
              </div>
            </div>
          )}
          {!!asset.disposal && (
            <>
              <div className="flex flex-col gap-x-6 gap-y-2 border-t border-t-black-16 pb-4 pt-4 md:flex-row md:pb-6 md:pt-6">
                <div className="text-black-70 md:w-60 md:shrink-0 md:text-300">
                  Disposal Date
                </div>
                <div className="text-black-100 md:text-300">
                  {format(new Date(asset.disposal.date), "dd.MM.yyyy")}
                </div>
              </div>
              {!!asset.disposal.cost && (
                <div className="flex flex-col gap-x-6 gap-y-2 border-t border-t-black-16 pb-4 pt-4 md:flex-row md:pb-6 md:pt-6">
                  <div className="text-black-70 md:w-60 md:shrink-0 md:text-300">
                    Disposal Cost
                  </div>
                  <div className="capitalize text-black-100 md:text-300">
                    {formatAmount(asset.disposal.cost)}
                  </div>
                </div>
              )}
              {!!asset.disposal.comment && (
                <div className="flex flex-col gap-x-6 gap-y-2 border-t border-t-black-16 pb-4 pt-4 md:flex-row md:pb-6 md:pt-6">
                  <div className="text-black-70 md:w-60 md:shrink-0 md:text-300">
                    Disposal Comment
                  </div>
                  <div className="capitalize text-black-100 md:text-300">
                    {asset.disposal.comment}
                  </div>
                </div>
              )}
            </>
          )}
          {!asset.attachments?.filter(
            (attachment) => attachment.type === "loan_agreement",
          ).length ? null : (
            <>
              <div className="pb-8 pt-10 text-300 font-bold text-black-100 md:pb-6 md:text-400">
                {getLabelFileAgreementField(asset.type, asset.purchaseType)}
              </div>
              <div className="flex flex-col gap-y-4">
                {asset.attachments
                  .filter((attachment) => attachment.type === "loan_agreement")
                  .map((attachment) => (
                    <FileItem
                      key={attachment.uniqueId}
                      file={attachment}
                      onPreview={setAttachmentPreview}
                      isEditing={false}
                    />
                  ))}
              </div>
            </>
          )}
          {!asset.attachments?.filter(
            (attachment) => attachment.type === "invoice",
          ).length ? null : (
            <>
              <div className="pb-8 pt-10 text-300 font-bold text-black-100 md:pb-6 md:text-400">
                Invoice
              </div>
              <div className="flex flex-col gap-y-4">
                {asset.attachments
                  .filter((attachment) => attachment.type === "invoice")
                  .map((attachment) => (
                    <FileItem
                      key={attachment.uniqueId}
                      file={attachment}
                      onPreview={setAttachmentPreview}
                      isEditing={false}
                    />
                  ))}
              </div>
            </>
          )}
          {!asset.disposal ||
          !asset.attachments?.filter((attachment) => !attachment.type)
            .length ? null : (
            <>
              <div className="pb-8 pt-10 text-300 font-bold text-black-100 md:pb-6 md:text-400">
                Disposal Receipts
              </div>
              <div className="flex flex-col gap-y-4">
                {asset.attachments
                  .filter((attachment) => !attachment.type)
                  .map((attachment) => (
                    <FileItem
                      key={attachment.uniqueId}
                      file={attachment}
                      onPreview={setAttachmentPreview}
                      isEditing={false}
                    />
                  ))}
              </div>
            </>
          )}
          {!asset.disposal && (
            <div className="mt-10 flex flex-col gap-y-4 border-t border-t-black-8 pb-2 md:flex-row md:justify-between md:pb-4 md:pt-4">
              <div className="flex flex-col gap-4 md:flex-row">
                <Button
                  leftIcon={<EditSvg />}
                  title="Edit Asset"
                  height="small"
                  disabled={!!asset.disposal}
                  dataTestid="button_editAsset"
                  onClick={onEdit}
                />
                <Button
                  variant="red"
                  leftIcon={<TrashXSvg />}
                  title="Remove from Business"
                  height="small"
                  disabled={!!asset.disposal}
                  dataTestid="button_disposeAsset"
                  onClick={() => setIsDisposing(true)}
                />
              </div>
              <Button
                variant="secondary"
                leftIcon={<XSvg />}
                title="Cancel"
                height="small"
                onClick={handleClose}
              />
            </div>
          )}
        </DrawerScrollableContainer>
        <FilePreview
          attachment={attachmentPreview}
          onClose={() => setAttachmentPreview(undefined)}
        />
        {isDisposing && (
          <AssetDisposal
            asset={asset}
            onClose={() => setIsDisposing(false)}
            onAssetDetailsClose={handleClose}
          />
        )}
      </Drawer>
    )
  );
};
